/* «Фотосессия товара» (APP-SPEC-5 §12; docs/03-api.md §6.6, §9.2 `pshoot`). Данные экрана — GET /app/studio/screens/pshoot:
   категории, сцены, наборы, площадки, форматы, «Сколько фото», чипы пожеланий, цена за кадр. Ключи заказа — поля формы. */
import type { StudioForm } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';
import { fieldOf, firstField, hasField, requiredGap, type Inputs } from '../shoot/kit/form-model';

export interface Scene {
  id: string;
  name: string;
  category: string;
  themes: string[];
  image: string | null;
  examples: string[];
  description: string;
  people: boolean;
  tags: string[];
  isNew: boolean;
  hit: boolean;
  popularity: number;
}
export interface SceneSet {
  id: string;
  name: string;
  category: string;
  scenes: string[];
  hit: boolean;
  isNew: boolean;
}
export interface PShootScreen {
  categories: Array<{ id: string; name: string }>;
  scenes: Scene[];
  sets: SceneSet[];
  platforms: Array<{ id: string; name: string; short: string; size: string }>;
  formats: Array<{ id: string; name: string; sub: string }>;
  counts: number[];
  wishes: string[];
  maxScenes: number;
  example: { before: string | null; after: string | null };
}
const strings = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && !!item) : []);
const idOf = (value: unknown) => (typeof value === 'number' ? String(value) : text(value));
const image = (value: Record<string, unknown>) => text(value.image_url) || text(value.cover_url) || text(value.url) || null;

export function pshootScreen(data: Record<string, unknown>): PShootScreen {
  return {
    categories: rows(data.categories).flatMap(item => (idOf(item.id) && text(item.name) ? [{ id: idOf(item.id), name: text(item.name) }] : [])),
    scenes: rows(data.scenes).flatMap(item =>
      idOf(item.id) && text(item.name)
        ? [
            {
              id: idOf(item.id),
              name: text(item.name),
              category: idOf(item.category) || idOf(item.category_id),
              themes: strings(item.themes),
              image: image(item),
              examples: strings(item.examples),
              description: text(item.description),
              people: item.people === true || item.with_model === true,
              tags: strings(item.tags),
              isNew: item.new === true,
              hit: item.hit === true,
              popularity: Number(item.popularity) || 0,
            },
          ]
        : [],
    ),
    sets: rows(data.sets).flatMap(item =>
      idOf(item.id) && text(item.name)
        ? [
            {
              id: idOf(item.id),
              name: text(item.name),
              category: idOf(item.category) || idOf(item.category_id),
              scenes: (Array.isArray(item.scenes) ? item.scenes : []).map(scene => idOf(typeof scene === 'object' && scene ? (scene as Record<string, unknown>).id ?? (scene as Record<string, unknown>).scene_id : scene)).filter(Boolean),
              hit: item.hit === true,
              isNew: item.new === true,
            },
          ]
        : [],
    ),
    platforms: rows(data.platforms).flatMap(item =>
      idOf(item.id) ? [{ id: idOf(item.id), name: text(item.name), short: text(item.short_name) || text(item.name), size: text(item.size) || text(item.sub) }] : [],
    ),
    formats: rows(data.formats).flatMap(item => (idOf(item.id) ? [{ id: idOf(item.id), name: text(item.name), sub: text(item.sub) || text(item.ratio) }] : [])),
    counts: (Array.isArray(data.counts) ? data.counts : []).map(Number).filter(value => Number.isInteger(value) && value > 0),
    wishes: strings(data.wishes),
    maxScenes: Number(data.max_scenes) || 4,
    example: { before: text(record(data.example).before_url) || null, after: text(record(data.example).after_url) || null },
  };
}

export const pshootKeys = (form: StudioForm | null) => ({
  category: hasField(form, 'category') ? 'category' : undefined,
  set: firstField(form, ['set_id', 'set'])?.key,
  scenes: hasField(form, 'scenes') ? 'scenes' : undefined,
  platform: hasField(form, 'platform') ? 'platform' : undefined,
  format: firstField(form, ['format', 'ratio'])?.key,
  count: firstField(form, ['count', 'variants'])?.key,
  model: hasField(form, 'model') ? 'model' : undefined,
  personId: hasField(form, 'person_id') ? 'person_id' : undefined,
  avatarId: hasField(form, 'avatar_id') ? 'avatar_id' : undefined,
  me: hasField(form, 'me') ? 'me' : undefined,
  wishes: firstField(form, ['wishes', 'text', 'description'])?.key,
  shoot: hasField(form, 'shoot') ? 'shoot' : undefined,
});

/** «Сколько фото» поровну на сцены, остаток — первой (§12.1 «Набор»). */
export function splitScenes(scenes: string[], count: number) {
  if (!scenes.length) return [];
  const base = Math.floor(count / scenes.length);
  const rest = count - base * scenes.length;
  return scenes.map((scene_id, index) => ({ scene_id, count: base + (index < rest ? 1 : 0) })).filter(item => item.count > 0);
}

/** Сколько кадров с моделью: по одному кадру на каждую долю сцены «С моделью». */
export function modelShots(screen: PShootScreen, scenes: Array<{ scene_id: string; count: number }>) {
  return scenes.reduce((sum, item) => sum + (screen.scenes.find(scene => scene.id === item.scene_id)?.people ? item.count : 0), 0);
}

export function pshootMissing({
  form,
  inputs,
  photoFields,
  rights,
  reference,
  referenceChecking,
  scenes,
  modelNeedsPhoto,
  wishLimit,
  priceConfirmed,
}: {
  form: StudioForm;
  inputs: Inputs;
  photoFields: string[];
  rights: boolean;
  reference: boolean;
  referenceChecking: boolean;
  scenes: number;
  modelNeedsPhoto: boolean;
  wishLimit: number;
  priceConfirmed: boolean;
}): string | null {
  const keys = pshootKeys(form);
  const ready = photoFields.filter(key => !!inputs[key]).length;
  if (!ready) return photoFields.length ? 'Загрузите фото товара' : 'Загрузка фото товара сейчас недоступна';
  if (!rights) return 'Подтвердите право использовать фото товара';
  if (referenceChecking) return 'Проверяем фото';
  if (keys.set && !reference && !inputs[keys.set]) return 'Выберите набор сцен';
  if (keys.scenes && !reference && !scenes) return 'Выберите сцены';
  if (modelNeedsPhoto) return 'Добавьте фото того, кто в кадре';
  const wishes = keys.wishes ? inputs[keys.wishes] ?? '' : '';
  if (Array.from(wishes).length > wishLimit) return 'Пожелания длиннее допустимого';
  if (keys.wishes && fieldOf(form, keys.wishes)?.required && !wishes.trim()) return 'Добавьте пожелания';
  if (!priceConfirmed) return 'Цена такого набора пока не подтверждена: обновите форму';
  return requiredGap(form, inputs, [...photoFields, ...Object.values(keys).filter((key): key is string => !!key)]);
}
